import clsx from 'clsx';
import { OutrightMarket, SportMatchModel } from '@nf/types/odds';
import { newbieMatchStyle } from './newbie-match.style';
import { useAtomValue } from 'jotai';
import { getHomeTeamName, getTeamName } from '@/utils/odds-utils';
import { oddsPageAtom } from '@/atoms/odds-page';
import { useTranslations } from 'next-intl';
import { useMatchBetTypes } from '@/features/odds-page/hooks/use-match-bet-types';
import { useEffect, useMemo, useState } from 'react';
import { CollapseMatchInfo } from '../collapse-match-info';
import { matchEquals } from '@nf/utils-common/fpw/monadic';
import { MatchOutright } from '../match/match-outright';
import { NewbieMatchEvent } from './newbie-match-event';
import { NewbieMatchInfo } from './newbie-match-info';
import { ScoreItem, scoreboardService } from '@/services/scoreboard.service';
import { itemsEmptyCheck, stringEmptyCheck } from '@nf/utils-common/fpw/utils';
import { useOddsTableMarsContext } from '../../context';
import { getMatchMoreBetTypeCount } from '@/utils/match-util';
import { useSiteStore } from '@/store/site';

interface MatchProps {
	match: SportMatchModel;
	isDefaultExpand: boolean;
};


export const NewbieMatch = ({
	match,
	isDefaultExpand,
}: MatchProps) => {
	const { sport, gameType } = useOddsTableMarsContext();
	const {
		TeamId1,
		TeamId2,
	} = match;
	const oddsData = useAtomValue(oddsPageAtom);
	const [isShowMatch, setIsShowMatch] = useState(isDefaultExpand);
	const betTypeFilterCriterion = useSiteStore(state => state.getBetTypeFilterCriterion(sport));
	const t = useTranslations('General');
	const homeTeamName = getHomeTeamName(TeamId1, match.Neu, t('Neutral_Team'), oddsData?.teamNameMap);
	const awayTeamName = getTeamName(TeamId2, oddsData?.teamNameMap);
	const { markets, hasGetMarket } = useMatchBetTypes({
		match: match,
		isFetchFromGetMarket: isShowMatch,
		sportId: sport,
		betTypeClass: 'OU'
	});
	const [homeScoreList, awayScoreList] = useMemo(() => [
		scoreboardService.getMatchCricketScore(match, 'home'),
		scoreboardService.getMatchCricketScore(match, 'away')
	], [match])

	useEffect(() => {
		if (isDefaultExpand) {
			setIsShowMatch(true);
		}
	}, [isDefaultExpand]);

	const teamScoreList = useMemo(() => scoreboardService.getAllSportScore(match), [match]);
	const getFirstScoreText = (scoreList: ScoreItem[]): string => itemsEmptyCheck(scoreList)
		.chain((scoreList: ScoreItem[]) => stringEmptyCheck(scoreList[0]?.text))
		.fold(
			() => '',
			(teamScore: string) => teamScore
		)

	const [teamAScore, teamBScore] = matchEquals(sport)
		.on(50, () => [
			getFirstScoreText(homeScoreList),
			getFirstScoreText(awayScoreList)]
		)
		.on(44, () => [
			`${t('lbl_round')}${getFirstScoreText(teamScoreList)}`,
			'']
		)
		.otherwise(() => getFirstScoreText(teamScoreList).split('-'))

	return (
		<>
			<CollapseMatchInfo
				match={match}
				isOpen={isShowMatch}
				toggleMatch={() => setIsShowMatch(!isShowMatch)}
				homeTeamName={homeTeamName}
				awayTeamName={awayTeamName}
			/>
			{
				isShowMatch && hasGetMarket && matchEquals(sport)
					.on(999, () => (
						<MatchOutright
							match={match}
							markets={markets as OutrightMarket[]}
							teamNameMap={oddsData?.teamNameMap}
						/> // TODO: outright
					))
					.otherwise(() => (
						<div className={clsx('newbie-match', newbieMatchStyle.match)}>
							<NewbieMatchEvent
								markets={markets}
								homeTeamName={homeTeamName}
								awayTeamName={awayTeamName}
								match={match}
								isLoading={!hasGetMarket}
								teamAScore={teamAScore}
								teamBScore={teamBScore}
							/>
							<NewbieMatchInfo match={match} betTypeCount={getMatchMoreBetTypeCount(match, markets, gameType)} />
						</div>)
					)
			}
		</>
	)
}

